import axios from "axios";
import Cookies from "js-cookie";
import { createHmac } from "crypto";
import {
  decryptResBody,
  encryptReqBody,
  encryptReqHeader,
} from "@/utils/useDecrypt";
import {
  deleteAllCookies,
  getCookieDecryptToken,
  parseJwt,
  tokenCookieName,
} from "@/utils/cookie";
import store, { persistor } from "@/store/store";

export const MainApi = "/api";

const instance = axios.create({
  baseURL: `${MainApi}/`,
});

instance.interceptors.request.use(
  async (config: any) => {
    config.meta = config.meta || {};
    config.meta.requestStartedAt = new Date().getTime();
    const token = getCookieDecryptToken();
    const lang = Cookies.get("lang") || "uz";
    const user_id = token && (parseJwt(token as any)?.user_id as number);
    const h = user_id
      ? user_id ^ +process.env.NEXT_PUBLIC_USER_HASH_XOR_VALUE!
      : null;

    const timeConfig = store.getState().settings;

    const x_timestamp =
      Math.floor(Date.now() / 1000) +
      timeConfig.timeDifference +
      timeConfig?.allowTime;

    const bodyStr = JSON.stringify(config.data);

    const s = createHmac("sha256", process.env.NEXT_PUBLIC_HMAC_HASHING_SECRET!)
      .update(
        x_timestamp +
          bodyStr +
          x_timestamp +
          process.env.NEXT_PUBLIC_HMAC_SECRET! +
          bodyStr
      )
      .digest("hex");

    const xDataPayload = {
      u: token,
      s,
      h,
      t: x_timestamp,
      l: lang,
    };

    const isDev = process.env.NEXT_PUBLIC_MODE === "dev";

    config.headers = config.data?.query_params?.token
      ? {
          "Content-Type": "application/json",
          ...config.headers,
          timeout: 5000,
          n: isDev ? config.data?.action : null,
          t: isDev ? token : null,
          a: await encryptReqHeader(xDataPayload),
          "Candidate-Authorization": config.data?.query_params?.token,
        }
      : {
          "Content-Type": "application/json",
          ...config.headers,
          timeout: 5000,
          n: isDev ? config.data?.action : null,
          t: isDev ? token : null,
          a: await encryptReqHeader(xDataPayload),
        };
    config.data = isDev
      ? { a: await encryptReqBody(config.data), body: config.data }
      : {
          a: await encryptReqBody(config.data),
        };
    return config;
  },
  (error) => Promise.reject(error.response)
);

instance.interceptors.response.use(
  async (response: any) => {
    if (response.data && response.data.a) {
      response.data = await decryptResBody(response.data.a);
    }
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      Cookies.remove(tokenCookieName);
      await persistor.purge();
      store.dispatch({ type: "RESET_APP" });
      deleteAllCookies();
    }

    // Only decrypt if error.response.data.a exists
    let err = null;
    if (error.response?.data?.a) {
      err = await decryptResBody(error.response.data.a);
    }

    const errResponse = {
      ...error?.response,
      data: err || error.response?.data || { message: "An error occurred" },
    };
    return Promise.reject(errResponse);
  }
);

export default instance;
